import React, { useState } from "react";
import { ArrowLeft, AlertCircle, Shield, User } from "lucide-react";
import { usePaymentApi } from "../hooks/use-payment-api";
import messageHandler from "../../../../utils/messageHandler";
import { Input } from "../../../../shadcn/ui/input";
import { Button } from "../../../../shadcn/ui/button";
import { Label } from "../../../../shadcn/ui/label";
import { PhoneInput } from "../../../../components/ui/phone-input";

const MpesaTillForm = ({ onPrevious, onComplete, existingData = null }) => {
  const { loading, submitPaymentSetup } = usePaymentApi();
  const [formData, setFormData] = useState({
    paymentMethod: "till",
    tillNumber: existingData?.tillNumber || "123456",
    tillName: existingData?.tillName || "Quick Mart Till",
    businessPhoneNumber: existingData?.businessPhoneNumber || "+254733444555",
    businessEmailAddress: existingData?.businessEmailAddress || "till@quickmart.co.ke",
    contactPersonName: existingData?.contactPersonName || "Sarah Manager",
    contactPersonPhone: existingData?.contactPersonPhone || "+254744555666",
    contactPersonIdNumber: existingData?.contactPersonIdNumber || "98765432",
  });

  const handleSubmit = async () => {
    try {
      const result = await submitPaymentSetup(formData);
      if (result) {
        messageHandler.showApiMessage(result, "payment setup");
        onComplete();
      } else {
        messageHandler.showApiMessage({ message: "Payment setup failed" }, "payment setup");
      }
    } catch (error) {
      messageHandler.showApiMessage(error, "payment setup");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <h1 className="text-xl font-semibold">Payment Setup</h1>
        <div className="mt-2 w-full h-1 bg-gray-200">
          <div className="w-full h-1 bg-black"></div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-6">
          <h2 className="mb-2 text-lg font-medium">Payment Setup</h2>
          <p className="text-gray-600">Set up your payment method for receiving payments</p>
        </div>

        <div className="mb-6">
          <h3 className="mb-2 font-medium">Payment Method</h3>
          <p className="mb-4 text-gray-600">
            Select how you would like to receive payments. This will be synced with our internal system.
          </p>

          {/* Selected Method Display */}
          <div className="p-4 mb-6 bg-green-50 rounded-lg border">
            <div className="flex items-center">
              <input type="radio" checked readOnly className="mr-3" />
              <div className="flex items-center">
                <div className="flex justify-center items-center mr-3 w-8 h-8 bg-green-100 rounded">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                </div>
                <div>
                  <div className="font-medium">M-Pesa Till Number</div>
                  <div className="text-sm text-gray-600">Receive payments via M-Pesa Till number</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* M-Pesa Till Details */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <Shield className="mr-2 w-5 h-5" />
            <h3 className="font-medium">M-Pesa Till Details</h3>
          </div>
          <p className="mb-4 text-gray-600">Provide your M-Pesa Till information for payment processing</p>

          <div className="p-3 mb-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-800">
                Ensure your Till number is active and properly configured for receiving payments.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block mb-2 text-sm font-medium">
                Till Number <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter your Till number"
                className="w-full"
                value={formData.tillNumber}
                onChange={e => handleInputChange("tillNumber", e.target.value)}
              />
            </div>
            <div>
              <Label className="block mb-2 text-sm font-medium">
                Till Name <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter Till name/business name"
                className="w-full"
                value={formData.tillName}
                onChange={e => handleInputChange("tillName", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block mb-2 text-sm font-medium">
                Business Phone Number <span className="text-red-500">*</span>
              </Label>
              <PhoneInput
                defaultCountry="ke"
                value={formData.businessPhoneNumber}
                onChange={value => handleInputChange("businessPhoneNumber", value)}
              />
            </div>
            <div>
              <Label className="block mb-2 text-sm font-medium">
                Business Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                type="email"
                placeholder="Enter business email address"
                className="w-full"
                value={formData.businessEmailAddress}
                onChange={e => handleInputChange("businessEmailAddress", e.target.value)}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" />
              <span className="text-sm">I confirm that this Till number is active and operational</span>
            </label>
          </div>
        </div>

        {/* Contact Person Details */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <User className="mr-2 w-5 h-5" />
            <h3 className="font-medium">Contact Person Details</h3>
          </div>
          <p className="mb-4 text-gray-600">Primary contact for payment and verification purposes</p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block mb-2 text-sm font-medium">
                Contact Person Name <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter contact person full name"
                className="w-full"
                value={formData.contactPersonName}
                onChange={e => handleInputChange("contactPersonName", e.target.value)}
              />
            </div>
            <div>
              <Label className="block mb-2 text-sm font-medium">
                Contact Person Phone <span className="text-red-500">*</span>
              </Label>
              <PhoneInput
                defaultCountry="ke"
                value={formData.contactPersonPhone}
                onChange={value => handleInputChange("contactPersonPhone", value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <Label className="block mb-2 text-sm font-medium">
              Contact Person ID/Passport Number <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter ID or passport number"
              className="w-full"
              value={formData.contactPersonIdNumber}
              onChange={e => handleInputChange("contactPersonIdNumber", e.target.value)}
            />
          </div>
        </div>

        {/* Verification Notice */}
        <div className="mb-6">
          <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Verification Required</p>
                <p className="mt-1 text-sm text-yellow-700">
                  Till number payments may require additional verification. Our team will contact you if additional
                  documentation is needed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onPrevious} className="flex items-center text-gray-600 hover:text-gray-800">
            <ArrowLeft className="mr-2 w-4 h-4" />
            Previous
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="flex items-center">
            {loading ? "Processing..." : existingData ? "Update Payment Method" : "Complete Setup"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MpesaTillForm;
